import { observable, computed, action, runInAction, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import {
  OffsetPaged,
  CursorPaged,
  EncodedCursor,
  CursorPageQuery,
  OffsetPageQuery,
} from '../types/definitions'
import memoizee from 'memoizee'
import {
  CancellationToken,
  Cancelled,
  createCancellationTokenSource,
} from 'infra/cancellation'

export type FetchItems<T, F = Record<string, unknown>> = (
  params: CursorPageQuery,
  cancellationToken: CancellationToken,
  filter?: F,
) => Promise<CursorPaged<T>>

export interface InfiniteScrollProviderOptions<T, F> {
  fetchItems: FetchItems<T, F>
  memoizeInitial?: boolean
  limit: number
  startFetchTaskAsPending?: boolean
}

export default class InfiniteScrollProvider<T, F = Record<string, unknown>> {
  /**
   * Whether we have paged the entire data set.
   */
  @observable isDone = false
  /**
   * The current cursor.
   */
  @observable cursor: EncodedCursor | null = null
  /**
   * How many to return at a time.
   */
  @observable limit = 10
  /**
   * The total available records given the current filter.
   */
  @observable total = 0
  /**
   * All the items collected.
   */
  @observable items: T[] = []

  private lastPromise: Promise<OffsetPaged<T>> | null = null
  private fetchItems: FetchItems<T, F>
  private filter: F | undefined = undefined
  private cancellation = createCancellationTokenSource()

  constructor({
    fetchItems,
    memoizeInitial,
    limit,
    startFetchTaskAsPending,
    ...opts
  }: InfiniteScrollProviderOptions<T, F>) {
    makeObservable(this)
    Object.assign(this, opts)
    runInAction(() => {
      this.limit = limit
      if (startFetchTaskAsPending) {
        this.fetch.setState({ state: 'pending' })
      }
    })
    this.fetchItems = fetchItems
    this.more = this.more.bind(this)
    if (memoizeInitial) {
      this.fetch = this.fetch.wrap((fn) => memoizee(fn, { promise: true }))
    }
  }

  @computed
  get active() {
    return this.fetch.resolved && this.more.resolved && !this.isDone
  }

  /**
   * Resets the paging state and fetch cache (unless `soft` is `true`)
   * @param {Boolean} soft If true, does not clear the initial fetch cache.
   */
  @action.bound
  reset(soft: boolean) {
    this.isDone = false
    this.cursor = null
    this.total = 0
    this.items = []
    if ((this.fetch as any).clear && !soft) {
      ;(this.fetch as any).clear()
    }
  }

  @action.bound
  done() {
    this.isDone = true
  }

  @action.bound
  receiveResult(result: CursorPaged<T>) {
    this.cursor = result.cursor
    this.total = result.total || 0
  }

  /**
   * Removes elements based on the predicate.
   */
  @action.bound
  remove(predicate: (t: T) => boolean) {
    this.items = this.items.filter((x) => !predicate(x))
  }

  fetch = task.resolved(async (filter?: F) => {
    this.reset(true)
    this.filter = filter
    return this.more()
  })

  more = task.resolved(async () => {
    this.cancellation.cancel()
    this.cancellation = createCancellationTokenSource()
    const promise = (this.lastPromise = this.invokeFetchItems(
      this.cancellation.token,
    ))
    const result = await promise
    if (promise !== this.lastPromise) {
      // Cancelled
      return null
    }

    if (!result) {
      return null
    }

    if (!result.cursor) {
      this.done()
    } else {
      this.receiveResult(result)
    }

    return result
  })

  private async invokeFetchItems(token: CancellationToken) {
    const after = this.cursor ? this.cursor : undefined

    return this.fetchItems(
      {
        after,
        limit: this.limit,
      },
      token,
      this.filter,
    )
      .then((result) => {
        token.throwIfCancelled()
        runInAction(() => {
          if (!after) {
            this.items = []
          }
          this.items.push(...result.data)
        })
        return result
      })
      .catch((err) => {
        if (err instanceof Cancelled || token.isCancelled) {
          return CursorPaged.empty
        }
        throw err
      })
  }
}

/**
 * Callback for fetching items using skip+limit.
 */
export type OffsetFetchItems<T, F = Record<string, unknown>> = (
  params: OffsetPageQuery,
  cancellationToken: CancellationToken,
  filter?: F,
) => Promise<T[] | OffsetPaged<T>>
